import React, { useState, useEffect, useCallback } from "react";
import { useAuthStore } from "@/stores/authStore";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { FundRawRecord } from "./types";
import { getFundDisplayName } from "@/utils/fundDisplayUtils";
import { usePortfolioManagement } from "@/hooks/usePortfolioManagement";
import PortfolioSelector from "./portfolio/PortfolioSelector";
import PortfolioCreator from "./portfolio/PortfolioCreator";
import { Progress } from "@/components/ui/progress";
import { hydrateFundDetails } from "./portfolio/addToPortfolioService";
import { useBulkAddToPortfolio } from "./portfolio/useBulkAddToPortfolio";

interface AddToPortfolioDialogProps {
  isOpen: boolean;
  onClose: () => void;
  fund?: FundRawRecord | null;
  funds?: FundRawRecord[];
  onSuccess?: () => void;
}

export default function AddToPortfolioDialog({
  isOpen,
  onClose,
  fund,
  funds,
  onSuccess,
}: AddToPortfolioDialogProps) {
  // Add state to store the full fund details we may need to fetch
  const [fullFundDetails, setFullFundDetails] = useState<any>(null);
  
  // UI state for popovers
  const [isComboOpen, setIsComboOpen] = useState(false);
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  
  // Progress tracking for bulk operations
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [bulkResults, setBulkResults] = useState<{ success: number; failed: number; errors: string[] }>({ success: 0, failed: 0, errors: [] });
  
  // Determine if we're in bulk mode
  const isBulkMode = Boolean(funds && (funds?.length || 0) > 0);
  const [isProcessing, setIsProcessing] = useState(false);

  // Memoize callbacks to prevent infinite loops
  const memoizedOnSuccess = useCallback(() => {
    if (onSuccess) onSuccess();
  }, [onSuccess]);

  const memoizedOnClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  // Use our portfolio management hook for the core logic
  const {
    isCreatingNew,
    newPortfolioName,
    portfolioType, 
    selectedPortfolioId,
    portfolios,
    isLoading,
    errorMessage,
    setIsCreatingNew,
    setNewPortfolioName,
    setPortfolioType,
    setSelectedPortfolioId,
    fetchPortfolios,
    addToPortfolio,
    handleClose,
  } = usePortfolioManagement({ onSuccess: memoizedOnSuccess, onClose: memoizedOnClose });

  // Centralized auth gating
  const userId = useAuthStore((s) => s.user?.id);

  // Effect to reset processing state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setIsProcessing(false);
    }
  }, [isOpen]);

  // Use a local wrapper for getFundDisplayName that prefers fullFundDetails when available
  const getDisplayName = (fundParam: any) => {
    // First check if we should use our fetched full fund details
    const fundToUse = fullFundDetails || fundParam;
    return getFundDisplayName(fundToUse);
  };

  // Effect to ensure we have full details AND a classification for the selected fund
  useEffect(() => {
    if (!isOpen || !fund?.id) return;
    const base = fullFundDetails || fund;
    if (!fullFundDetails) setFullFundDetails(base);
    (async () => {
      const updated = await hydrateFundDetails(fund.id as string, base);
      if (updated) setFullFundDetails(updated);
    })();
    // We intentionally exclude fullFundDetails from deps to avoid loops
  }, [isOpen, fund]);
  
  // Effect to fetch portfolios when dialog is opened
  useEffect(() => {
    if (isOpen) {
      fetchPortfolios();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset fullFundDetails when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setFullFundDetails(null);
    }
  }, [isOpen]);

  // Handle bulk add to portfolio
  const handleBulkAddToPortfolio = useBulkAddToPortfolio({
    funds,
    isCreatingNew,
    newPortfolioName,
    portfolioType,
    selectedPortfolioId,
    portfolios,
    userId,
    setIsProcessing,
    setBulkProgress,
    setBulkResults,
    onSuccess: memoizedOnSuccess,
    handleClose,
  });

  // Handle single fund add to portfolio
  const addSingleFund = (fundToAdd: FundRawRecord) => {
    addToPortfolio(fundToAdd);
  };

  // Handle Add to Portfolio button click
  const handleAddToPortfolio = () => {
    // Use fullFundDetails if available, otherwise fall back to fund prop
    const fundToUse = fullFundDetails || fund;
    if (fundToUse) {
      addToPortfolio(fundToUse);
    }
  };

  // Don't render anything if dialog is not open to avoid mounting issues
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Add to Portfolio</DialogTitle>
          <DialogDescription>
            {isBulkMode 
              ? `Add ${funds?.length || 0} ${funds?.length === 1 ? 'fund' : 'funds'} to an existing portfolio or create a new one.`
              : `Add ${getDisplayName(fund)} to an existing portfolio or create a new one.`
            }
          </DialogDescription>
        </DialogHeader>

        {/* Display error message if any */}
        {errorMessage && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
            {errorMessage}
          </div>
        )}
        
        {/* Progress indicator for bulk operations */}
        {isBulkMode && isProcessing && bulkProgress.total > 0 && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-blue-900">
                Processing {bulkProgress.current} of {bulkProgress.total} items...
              </span>
              <span className="text-sm text-blue-700">
                {Math.round((bulkProgress.current / bulkProgress.total) * 100)}%
              </span>
            </div>
            <Progress 
              value={(bulkProgress.current / bulkProgress.total) * 100} 
              className="h-2"
            />
            {bulkResults.success > 0 || bulkResults.failed > 0 ? (
              <div className="mt-2 text-xs text-blue-700">
                ✓ {bulkResults.success} added
                {bulkResults.failed > 0 && (
                  <span className="ml-2">⚠ {bulkResults.failed} failed</span>
                )}
              </div>
            ) : null}
          </div>
        )}

        <div className="grid gap-4 py-4">
          {!isCreatingNew ? (
            <PortfolioSelector
              portfolios={portfolios}
              selectedPortfolioId={selectedPortfolioId || ""}
              setSelectedPortfolioId={setSelectedPortfolioId}
              onCreateNew={() => { setPortfolioType('personal'); setIsCreatingNew(true); }}
              isLoading={isLoading}
              isComboOpen={isComboOpen}
              setIsComboOpen={setIsComboOpen}
            />
          ) : (
            <PortfolioCreator
              newPortfolioName={newPortfolioName}
              setNewPortfolioName={setNewPortfolioName}
              portfolioType={portfolioType}
              setPortfolioType={setPortfolioType}
              onBack={() => setIsCreatingNew(false)}
              isLoading={isLoading}
              isTypeOpen={isTypeOpen}
              setIsTypeOpen={setIsTypeOpen}
            />
          )}
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={isBulkMode ? handleBulkAddToPortfolio : handleAddToPortfolio}
            disabled={isLoading || isProcessing || (isCreatingNew ? !newPortfolioName : !selectedPortfolioId)}
          >
            {(isLoading || isProcessing) 
              ? "Adding..." 
              : isBulkMode 
                ? `Add ${funds?.length || 0} ${funds?.length === 1 ? 'Fund' : 'Funds'} to Portfolio`
                : "Add to Portfolio"
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Function is already exported as default at its declaration
